import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { auth } from '../services/api';
import { resetSessionExpiredFlag } from '../utils/sessionHandler';

interface UserData {
  id: number;
  phone_number: string;
  email: string | null;
  full_name: string | null;
  is_active: boolean;
  is_verified: boolean;
  language_preference: string;
  created_at: string;
  updated_at: string;
}

interface AuthData {
  provider: string;
  phone_verified: boolean;
  google_email: string | null;
}

interface OnboardingData {
  full_name: string;
  position: string;
  business_name: string;
  business_type: string;
  business_size: string;
  business_description: string;
  service_type: string;
  service_description: string;
  target_audience: string;
  channels: string;
  instagram_username: string;
  subscription_plan: string;
  payment_status: string;
  step: number;
  is_completed: boolean;
}

interface MembershipData {
  is_member: boolean;
  ui_modules?: string[];
}

export interface User {
  user: UserData;
  auth: AuthData;
  onboarding: OnboardingData;
  membership?: MembershipData;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (phoneNumber: string, otpCode: string) => Promise<User>;
  signup: (phoneNumber: string, otpCode: string) => Promise<User>;
  loginWithEmail: (email: string, otpCode: string) => Promise<User>;
  signupWithEmail: (email: string, otpCode: string) => Promise<User>;
  loginWithTwoFactor: (phoneNumber: string, twoFactorCode: string) => Promise<User>;
  logout: () => void;
  sendOTP: (phoneNumber: string, type: 'signin' | 'signup', languagePreference?: string, utm_source?: string, utm_medium?: string, utm_campaign?: string, utm_term?: string, utm_content?: string) => Promise<void>;
  sendSignupEmailRequest: (email: string, languagePreference?: string, utm_source?: string, utm_medium?: string, utm_campaign?: string, utm_term?: string, utm_content?: string) => Promise<void>;
  sendSigninEmailRequest: (email: string) => Promise<void>;
  getCurrentUser: () => Promise<void>;
  currentLanguage: string;
  setCurrentLanguage: (lang: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Check domain first and set language in localStorage
    const domain = window.location.hostname;
    let language = 'fa'; // default
    
    if (domain === 'app.aidasales.net') {
      language = 'en';
      localStorage.setItem('language', 'en');
    } else if (domain === 'app.aidasales.ir') {
      language = 'fa';
      localStorage.setItem('language', 'fa');
    } else {
      // Fallback to localStorage or default to 'fa'
      language = localStorage.getItem('language') || 'fa';
    }
    
    return language;
  });

  // Ensure language is set in localStorage based on domain on mount
  useEffect(() => {
    const domain = window.location.hostname;
    if (domain === 'app.aidasales.net') {
      localStorage.setItem('language', 'en');
      if (currentLanguage !== 'en') {
        setCurrentLanguage('en');
      }
    } else if (domain === 'app.aidasales.ir') {
      localStorage.setItem('language', 'fa');
      if (currentLanguage !== 'fa') {
        setCurrentLanguage('fa');
      }
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user?.user?.language_preference) {
      setCurrentLanguage(user.user.language_preference);
      localStorage.setItem('language', user.user.language_preference);
    }
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await auth.getCurrentUser();
        setUser(response.data);
        // Set language from user preference
        if (response.data.user.language_preference) {

          setCurrentLanguage(response.data.user.language_preference);
          localStorage.setItem('language', response.data.user.language_preference);
        }
      }
    } catch (err) {
      console.error('AuthContext: Auth check failed:', err);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const sendOTP = useCallback(async (phoneNumber: string, type: 'signin' | 'signup', languagePreference: string = currentLanguage, utm_source: string = '', utm_medium: string = '', utm_campaign: string = '', utm_term: string = '', utm_content: string = '') => {
    try {
      setLoading(true);
      setError(null);
      if (type === 'signup') {
        await auth.sendSignupOTP(phoneNumber, languagePreference, utm_source, utm_medium, utm_campaign, utm_term, utm_content);
      } else {
        await auth.sendSigninOTP(phoneNumber);
      }
    } catch (err) {
      setError('خطا در ارسال کد تایید');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentLanguage]);

  const sendSignupEmailRequest = useCallback(async (email: string, languagePreference: string = currentLanguage, utm_source: string = '', utm_medium: string = '', utm_campaign: string = '', utm_term: string = '', utm_content: string = '') => {
    try {
      setLoading(true);
      setError(null);
      await auth.sendSignupEmailOTP(email, languagePreference, utm_source, utm_medium, utm_campaign, utm_term, utm_content);
    } catch (err) {
      setError('خطا در ارسال کد تایید');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentLanguage]);

  const sendSigninEmailRequest = useCallback(async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      await auth.sendSigninEmailOTP(email);
    } catch (err) {
      setError('خطا در ارسال کد تایید');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (phoneNumber: string, otpCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await auth.verifySigninOTP(phoneNumber, otpCode);
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      const userResponse = await auth.getCurrentUser();
      setUser(userResponse.data);
      // Set language from user preference
      if (userResponse.data.user.language_preference) {
        setCurrentLanguage(userResponse.data.user.language_preference);
        localStorage.setItem('language', userResponse.data.user.language_preference);
      }
      // Reset session expired flag on successful login
      resetSessionExpiredFlag();
      return userResponse.data;
    } catch (err) {
      setError('کد تایید نامعتبر است');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setCurrentLanguage]);

  const signup = useCallback(async (phoneNumber: string, otpCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await auth.verifySignupOTP(phoneNumber, otpCode, currentLanguage);
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      const userResponse = await auth.getCurrentUser();
      setUser(userResponse.data);
      // Set language from user preference
      if (userResponse.data.user.language_preference) {
        setCurrentLanguage(userResponse.data.user.language_preference);
        localStorage.setItem('language', userResponse.data.user.language_preference);
      }
      // Reset session expired flag on successful signup
      resetSessionExpiredFlag();
      return userResponse.data;
    } catch (err: any) {
      // Let the global error handler handle this
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentLanguage, setCurrentLanguage]);

  const loginWithEmail = useCallback(async (email: string, otpCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await auth.verifySigninEmailOTP(email, otpCode);
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      const userResponse = await auth.getCurrentUser();
      setUser(userResponse.data);
      // Set language from user preference
      if (userResponse.data.user.language_preference) {
        setCurrentLanguage(userResponse.data.user.language_preference);
        localStorage.setItem('language', userResponse.data.user.language_preference);
      }
      // Reset session expired flag on successful login
      resetSessionExpiredFlag();
      return userResponse.data;
    } catch (err) {
      setError('کد تایید نامعتبر است');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setCurrentLanguage]);

  const signupWithEmail = useCallback(async (email: string, otpCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await auth.verifySigninEmailOTP(email, otpCode);
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      const userResponse = await auth.getCurrentUser();
      setUser(userResponse.data);
      // Set language from user preference
      if (userResponse.data.user.language_preference) {
        setCurrentLanguage(userResponse.data.user.language_preference);
        localStorage.setItem('language', userResponse.data.user.language_preference);
      }
      // Reset session expired flag on successful signup
      resetSessionExpiredFlag();
      return userResponse.data;
    } catch (err: any) {
      // Let the global error handler handle this
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setCurrentLanguage]);

  const loginWithTwoFactor = useCallback(async (phoneNumber: string, twoFactorCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await auth.verifySigninTwoFactor({ phone_number: phoneNumber, two_factor_auth_code: twoFactorCode });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      const userResponse = await auth.getCurrentUser();
      setUser(userResponse.data);
      // Set language from user preference
      if (userResponse.data.user.language_preference) {
        setCurrentLanguage(userResponse.data.user.language_preference);
        localStorage.setItem('language', userResponse.data.user.language_preference);
      }
      // Reset session expired flag on successful login
      resetSessionExpiredFlag();
      return userResponse.data;
    } catch (err) {
      setError('کد تایید نامعتبر است');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setCurrentLanguage]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
  }, []);

  const getCurrentUser = useCallback(async () => {
    try {
      const response = await auth.getCurrentUser();
      setUser(response.data);
      // Set language from user preference
      if (response.data.user.language_preference) {
        setCurrentLanguage(response.data.user.language_preference);
        localStorage.setItem('language', response.data.user.language_preference);
      }
    } catch (err) {
      console.error('AuthContext: Failed to get current user:', err);
      localStorage.removeItem('token');
      setUser(null);
      throw err;
    }
  }, [setCurrentLanguage]);

  const handleSetCurrentLanguage = useCallback(async (lang: string) => {
    setCurrentLanguage(lang);
    localStorage.setItem('language', lang);
    
    // Update user's language preference on the server if authenticated
    if (user) {
      try {
        await auth.updateUser({ language_preference: lang });
        await getCurrentUser(); // Refresh user data
      } catch (err) {
        console.error('Failed to update language preference:', err);
      }
    }
  }, [user, getCurrentUser, setCurrentLanguage]);

  const authContextValue = useMemo(
    () => ({
      user,
      loading,
      error,
      isAuthenticated: !!user,
      login,
      signup,
      loginWithEmail,
      signupWithEmail,
      loginWithTwoFactor,
      logout,
      sendOTP,
      sendSignupEmailRequest,
      sendSigninEmailRequest,
      getCurrentUser,
      currentLanguage,
      setCurrentLanguage: handleSetCurrentLanguage,
    }),
    [
      user,
      loading,
      error,
      login,
      signup,
      loginWithEmail,
      signupWithEmail,
      loginWithTwoFactor,
      logout,
      sendOTP,
      sendSignupEmailRequest,
      sendSigninEmailRequest,
      getCurrentUser,
      currentLanguage,
      handleSetCurrentLanguage,
    ],
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 